import React, { useState, useEffect, useRef, useMemo } from "react";
import useSWR from "swr";

// --- Utility Fetcher ---
const secureFetcher = (url) => fetch(url).then((res) => res.json());

// --- Helper: String to Safe ID (for scroll links) ---
const makeSafeId = (string) => {
  if (!string) return `section-${Math.random().toString(36).substr(2, 9)}`;
  return string.toLowerCase().replace(/[^a-z0-9]/g, "-");
};

// ============================================================================
// MAIN COMPONENT
// ============================================================================
export default function ProductsAndServices() {
  // 1. Fetch Data from API
  const {
    data: pageData,
    error,
    isLoading,
  } = useSWR(
    "https://47.activ8.digital/wp-json/wp/v2/pages/1205", // Aapki API endpoint
    secureFetcher,
  );

  const [activeTab, setActiveTab] = useState("");
  const sectionRefs = useRef({});

  // 2. Data Formatting, Sorting & Alternating Logic
  const sortedServices = useMemo(() => {
    if (!pageData?.acf?.general_summary) return [];

    // Step A: Extract and map data from ACF Flexible Content
    let services = pageData.acf.general_summary.map((item) => {
      const srv = item.general_summary; // Nested object inside ACF layout
      return {
        id: makeSafeId(srv.service_title),
        nav_label: srv.service_title,
        title: srv.service_title,
        description: srv.service_short_description,
        image_url: srv.service_image,
        btn_link: srv.learn_more_url,
        order: parseInt(srv.order_by) || 0,
        features: srv.features || [],
      };
    });

    // Step B: Sort by the "order_by" field (Ascending)
    services.sort((a, b) => a.order - b.order);

    // Step C: Apply Alternating Layouts & Backgrounds based on sorted index
    // Even index (0, 2, 4...) = Transparent & Image Left
    // Odd index (1, 3, 5...) = Light Blue & Image Right
    services = services.map((service, index) => {
      const isOddIndex = index % 2 !== 0;
      return {
        ...service,
        bg_class: isOddIndex ? "sky-ps-bg-light-blue" : "sky-ps-bg-transparent",
        layout_order: isOddIndex ? "image_right" : "image_left",
      };
    });

    return services;
  }, [pageData]);

  // Set initial active tab when data loads
  useEffect(() => {
    if (sortedServices.length > 0 && !activeTab) {
      setActiveTab(sortedServices[0].id);
    }
  }, [sortedServices, activeTab]);

  // 3. SMOOTH SCROLL LOGIC
  const scrollToSection = (id) => {
    setActiveTab(id);
    const element = document.getElementById(id);
    if (element) {
      // Offset added for sticky headers
      const offset = 100;
      const elementPosition =
        element.getBoundingClientRect().top + window.scrollY;
      window.scrollTo({
        top: elementPosition - offset,
        behavior: "smooth",
      });
    }
  };

  // 4. SCROLLSPY LOGIC
  useEffect(() => {
    if (sortedServices.length === 0) return;

    const observerOptions = {
      root: null,
      rootMargin: "-20% 0px -60% 0px",
      threshold: 0.1,
    };

    const observerCallback = (entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          setActiveTab(entry.target.id);
        }
      });
    };

    const observer = new IntersectionObserver(
      observerCallback,
      observerOptions,
    );

    Object.values(sectionRefs.current).forEach((ref) => {
      if (ref) observer.observe(ref);
    });

    return () => observer.disconnect();
  }, [sortedServices]);

  // Helper function to split title (e.g., "Sky47 Space" -> "Sky47" and "Space")
  const renderHighlightedTitle = (fullTitle) => {
    const words = fullTitle.split(" ");
    if (words.length > 1) {
      const firstWord = words[0];
      const restOfWords = words.slice(1).join(" ");
      return (
        <>
          {firstWord}{" "}
          <span className="sky-ps-title-highlight">{restOfWords}</span>
        </>
      );
    }
    return fullTitle;
  };

  // State Handling UI
  if (error)
    return (
      <div className="text-center py-5 text-danger">
        Error loading services data.
      </div>
    );
  if (isLoading)
    return (
      <div className="text-center py-5">
        <div className="spinner-border text-info"></div>
      </div>
    );
  if (sortedServices.length === 0) return null;

  // Header Validation (Show only if BOTH title and subtitle exist)
  const headerData = pageData?.acf?.header;
  const showHeader = headerData?.title && headerData?.subtitle;

  return (
    <div className="sky-ps-wrapper">
      {/* HEADER SECTION (CONDITIONAL) */}
      {showHeader && (
        <div className="container sky-ps-header">
          <h1 className="sky-ps-main-title">{headerData.title}</h1>
          <p className="sky-ps-subtitle">{headerData.subtitle}</p>
        </div>
      )}

      {/* HORIZONTAL STICKY NAVIGATION */}
      <div className="sky-ps-nav-container">
        <div className="sky-ps-nav-pill-box">
          {sortedServices.map((product) => (
            <button
              key={`nav-${product.id}`}
              className={`sky-ps-nav-item ${activeTab === product.id ? "active" : ""}`}
              onClick={() => scrollToSection(product.id)}
            >
              {product.nav_label}
            </button>
          ))}
        </div>
      </div>

      {/* DYNAMIC PRODUCT SECTIONS */}
      {sortedServices.map((product) => {
        // Layout Order Logic (Bootstrap 5 Order Classes)
        const isImageLeft = product.layout_order === "image_left";
        const imageColClass = isImageLeft
          ? "order-1 order-lg-1"
          : "order-1 order-lg-2";
        const textColClass = isImageLeft
          ? "order-2 order-lg-2"
          : "order-2 order-lg-1";

        return (
          <section
            key={`sec-${product.id}`}
            id={product.id}
            className={`sky-ps-section ${product.bg_class}`}
            ref={(el) => (sectionRefs.current[product.id] = el)}
          >
            <div className="container-custom">
              {" "}
              {/* Replace with 'container' if needed */}
              <div className="row align-items-center">
                {/* IMAGE COLUMN */}
                <div className={`col-lg-6 mb-5 mb-lg-0 ${imageColClass}`}>
                  <div className="sky-ps-image-wrapper">
                    <img
                      src={product.image_url}
                      alt={product.nav_label}
                      className="img-fluid"
                      loading="lazy"
                    />
                  </div>
                </div>

                {/* TEXT & FEATURES COLUMN */}
                <div className={`col-lg-6 ${textColClass}`}>
                  <div className="sky-ps-content-wrapper">
                    <h2 className="sky-ps-section-title">
                      {renderHighlightedTitle(product.title)}
                    </h2>

                    <p className="sky-ps-section-desc">{product.description}</p>

                    {/* Features Grid (2x2) */}
                    <div className="row">
                      {product.features.map((feature, fIndex) => (
                        <div
                          className="col-md-6 sky-ps-feature-box"
                          key={`feat-${fIndex}`}
                        >
                          <div className="sky-ps-icon-container">
                            {/* Rendering API Icon Image */}
                            {feature.feature_icon && (
                              <img
                                src={feature.feature_icon}
                                alt={feature.feature_title}
                                style={{
                                  width: "32px",
                                  height: "32px",
                                  marginBottom: "10px",
                                }}
                              />
                            )}
                          </div>
                          <h4 className="sky-ps-feature-title">
                            {feature.feature_title}
                          </h4>
                          <p className="sky-ps-feature-desc">
                            {feature.feature_short_description}
                          </p>
                        </div>
                      ))}
                    </div>

                    {/* Learn More Button */}
                    {product.btn_link && (
                      <div className="mt-3">
                        <a href={product.btn_link} className="white_btn">
                          Learn More
                        </a>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </section>
        );
      })}
    </div>
  );
}
